import clsx from 'clsx';

import { useState } from 'react';
import PrimaryButton from '../button/PrimaryButton';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { isEmailValid, isReportDescriptionValid } from './reportUtils';
import { FaCheck } from 'react-icons/fa6';
import pikkoCry from '../../styles/img/reports/pikkoCry.svg';
import { useCrashReport } from '../../context/CrashReportProvider';
import CrashReportModalSuccess from './CrashReportModalSuccess';
import { redirectAndReloadPage } from '../../services/sentryReport';
import { useFeedbackLogger } from '../../hooks/useFeedbackDrawer';
import { serializeCrashData } from '../../utils/errors/errorsUtils';

const HeaderModal = () => {
  return (
    <div className="flex flex-col items-center pt-8 pb-6 px-6 w-full">
      <div className="relative mb-4 animate-float">
        <img src={pikkoCry} alt="Pikko" className="w-64 h-64" />
      </div>

      <h1 className="geomanistMedium text-4xl mt-4 font-light text-white text-center mb-2 animate-text-appear">
        Pikko a un coup de mou...
      </h1>

      <p className="text-white text-center mt-2 text-2xl geomanistRegular animate-text-appear-delayed">
        Essayons de le réparer !
      </p>
    </div>
  )
}

const CrashReportModal = () => {
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector((state) => state.user);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { logUserFeedbackOnCrashReport } = useFeedbackLogger();
  const { isModalOpen, showSuccess, closeCrashReport, submitCrashReport, crashData } = useCrashReport();

  useEffect(() => {
    setEmail(user?.email || '');
  }, [user]);

  function reset () {
    setDescription('');
    setEmail(user?.email || '');
    setIsVisible(false);
  }

  const handleSubmit = async () => {
    if (!isReportDescriptionValid(description)) return;
    console.info('submitCrashReport', {
      description: description.trim(),
      email: email.trim() || null,
      crashData: crashData || null
    })

    setIsLoading(true);
    try {
      await submitCrashReport({
        description: description.trim(),
        email: email.trim() || null,
        crashData: crashData || null
      });

      const orderId = JSON.parse(localStorage.getItem('cartItems'))?._id;
      const crashDataSerialized = serializeCrashData(crashData);
      

      logUserFeedbackOnCrashReport(
        orderId,
          {
            description: description.trim(),
            email: email.trim() || null,
          },
        crashDataSerialized
      )

      reset()
    } catch (error) {
      console.error('Erreur lors de l\'envoi du rapport:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);

    setTimeout(() => {
      closeCrashReport();
      setIsClosing(false);
      setDescription('');
      setEmail(user?.email || '');
      setIsVisible(false);

      redirectAndReloadPage()
    }, 300);
  };

  const openEmailInput = () => {
    setIsVisible(true);
    setEmail(user?.email || '');
  };

  if (!isModalOpen && !isClosing) return null;

  return (
    <>
    <div
      className={`fixed inset-0 bg-[#262626] z-[200000]  flex items-center justify-top p-4 transition-all duration-300 ease-out overflow-y-auto  ${
        isClosing ? 'animate-fade-out opacity-0' : 'animate-fade-in opacity-100'
      }`}
    >

      <button
        type="button"
        onClick={handleClose}
        className="fixed top-8 right-8  py-3 text-gray-200 text-xl font-medium hover:text-gray-300 transition-all duration-200 hover:scale-105 active:scale-95">
        Passer
      </button>
      <div
        className={`rounded-2xl w-full max-w-[90%] mx-auto h-full flex flex-col items-center justify-start transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]   ${
          isClosing ? 'animate-modal-slide-out' : 'animate-modal-slide-in'
        }`}
      >
        {showSuccess ? (
          <CrashReportModalSuccess />
        ) : (
          <>
            <HeaderModal />

            <div className="mt-4 pb-6 w-full">
              <div className="mb-6 animate-slide-up">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="ex: j'ai cliqué sur le bouton 'Ajouter mes coordonnées' et ça a crashé"
                  className="w-full geomanistRegular text-2xl h-[25vh] min-h-40 p-8 border bg-[#727272] !border-[#ffffff46] ring-0 transition-all duration-300 rounded-xl resize-y text-white placeholder-gray-400 focus:outline-none focus:!border-white focus:ring-2 focus:ring-white focus:scale-[1.02] hover:shadow-lg"
                  maxLength={500}
                />

                <div className={`flex items-center relative -translate-y-8 h-0 transition-all duration-300 -translate-x-2 justify-end ${isReportDescriptionValid(description) ? 'opacity-100' : 'opacity-0 scale-50'}`}>
                  <div className="flex items-center justify-center w-8 h-8  bg-white rounded-full">
                    <FaCheck className="text-xl text-green-500" />
                  </div>
                </div>

              </div>

              <div className='flex flex-col items-center relative min-h-32 w-full animate-slide-up-delayed'>
                <button
                  type="button"
                  onClick={openEmailInput}
                  className={`flex items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 justify-center geomanistMedium w-full mb-6 text-white text-xl transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-105 active:scale-95 ${
                    isVisible ? 'animate-fade-out-scale opacity-0 pointer-events-none' : 'animate-fade-in-scale opacity-100'
                  }`}
                >
                  <span className="mr-2 text-4xl text-white transition-transform duration-300 hover:rotate-90">
                    +
                  </span>
                  <span className="text-2xl text-white">
                    Ajouter mes coordonnées
                  </span>
                </button>

                {isVisible && (
                  <div className="mb-6 w-full animate-email-slide-in">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ex: exemple@email.com"
                    className="w-full h-20 geomanistRegular text-2xl p-8 border bg-[#727272] !border-[#ffffff46] ring-0 transition-all duration-300 rounded-xl text-white !placeholder-gray-300 focus:outline-none focus:!border-white focus:ring-2 focus:ring-white focus:scale-[1.02] hover:shadow-lg"
                  />
                    <div className={`flex items-center relative -translate-y-8 h-0 transition-all duration-300 -translate-x-2 justify-end ${isEmailValid(email) ? 'opacity-100' : 'opacity-0 scale-50'}`}>
                      <div className="flex items-center justify-center w-8 h-8  bg-white rounded-full">
                      <FaCheck className="text-xl text-green-500" />
                      </div>
                    </div>
                  </div>
                )}
              </div>


              <div className="space-y-3 animate-slide-up-more-delayed mb-20">
                <div className="transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]">
                  <PrimaryButton
                    onClick={handleSubmit}
                    label="Envoyer à l'équipe"
                    isLoading={isLoading}
                    disabled={!isReportDescriptionValid(description)}
                    className={clsx("w-full", { "opacity-30": !isReportDescriptionValid(description) })}
                  />
                </div>
              </div>

            </div>
          </>
        )}
        </div>

      </div>
    </>
    );
  };

export default CrashReportModal;